import React from 'react'
import { useTranslation } from 'react-i18next'
import { Text, H3, Span, LoadingDots } from '@/components/atoms'
import {
  InfoBanner,
  PersonFormSection,
  PetFormSection,
} from '@/components/molecules'
import * as CoverageFeatureIcons from '@/components/atoms/icons/coverage-feature'
import { useRentersFormOne } from './page.onboarding-renters-form-one.hook'

interface RentersFormOnePageProps {
  onContinue: () => void
  onRegisterSubmit?: (fn: () => Promise<void>) => void
  onDisabledChange?: (disabled: boolean) => void
  onLoadingChange?: (loading: boolean) => void
}

export function RentersFormOnePage({
  onContinue,
  onRegisterSubmit,
  onDisabledChange,
  onLoadingChange,
}: RentersFormOnePageProps) {
  const { t } = useTranslation()
  const {
    roommateEntries,
    addRoommate,
    removeRoommate,
    updateRoommate,
    partnerEntries,
    addPartner,
    removePartner,
    updatePartner,
    petEntries,
    addPet,
    removePet,
    updatePet,
    getBreedOptions,
    isDataLoading,
    showErrors,
  } = useRentersFormOne(
    onContinue,
    onRegisterSubmit,
    onDisabledChange,
    onLoadingChange,
  )

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <div className="flex flex-col gap-4 md:hidden">
        <H3 className="text-[2rem]! text-neutral-900">
          <Span textSize="">
            {t('onboarding.renters.form.whoIsCovered.titleStart')}
          </Span>
          <br />
          <Span textSize="" className="text-indigo-base!">
            {t('onboarding.renters.form.whoIsCovered.titleHighlight')}
          </Span>
        </H3>
        <Text className="text-neutral-900">
          {t('onboarding.renters.form.whoIsCovered.description')}
        </Text>
      </div>

      <div className="flex flex-col gap-4 md:gap-8">
        <div className="flex flex-col gap-4 md:gap-6">
          <H3 className="text-[1.5rem] text-neutral-900">
            {t('onboarding.renters.form.whoIsCovered.whoElseLives')}
          </H3>
          <InfoBanner
            message={t('onboarding.renters.form.whoIsCovered.infoRoommates')}
          />
        </div>

        {isDataLoading ? (
          <div className="flex justify-center py-8">
            <LoadingDots />
          </div>
        ) : (
          <div className="flex flex-col gap-4 md:gap-8">
            <PersonFormSection
              entries={roommateEntries}
              title={() => t('onboarding.renters.form.whoIsCovered.roommates')}
              namePlaceholderKey="namePlaceholderRoommate"
              onAdd={addRoommate}
              onRemove={removeRoommate}
              onUpdate={updateRoommate}
              addLabel={t(
                'onboarding.renters.form.whoIsCovered.addAnotherRoommate',
              )}
              showAdd={false}
              showErrors={showErrors}
              icon={
                <CoverageFeatureIcons.RoommatesStreamlineIcon className="text-indigo-base h-8 w-8" />
              }
            />

            <PersonFormSection
              entries={partnerEntries}
              title={() => t('onboarding.renters.form.whoIsCovered.myPartner')}
              namePlaceholderKey="namePlaceholder"
              onAdd={addPartner}
              onRemove={removePartner}
              onUpdate={updatePartner}
              addLabel={t('onboarding.renters.form.whoIsCovered.addAnother')}
              showAdd={false}
              showErrors={showErrors}
              variant="spouse"
              icon={
                <CoverageFeatureIcons.PartnerStreamlineIcon className="text-indigo-base h-8 w-8" />
              }
            />

            <PetFormSection
              entries={petEntries}
              title={() => t('onboarding.renters.form.whoIsCovered.pet')}
              onAdd={addPet}
              onRemove={removePet}
              onUpdate={updatePet}
              getBreedOptions={getBreedOptions}
              addLabel={t('onboarding.renters.form.whoIsCovered.addAnotherPet')}
              showAdd={false}
              showErrors={showErrors}
              icon={
                <CoverageFeatureIcons.PetStreamlineIcon className="text-indigo-base h-8 w-8" />
              }
            />
          </div>
        )}
      </div>
    </div>
  )
}
